import { useMemo } from 'react';
import { ALL_ALERT_STATES, DOC_CATEGORIES } from './monitorConstants';

// Order-insensitive identity of a monitor's notify targets: [A,D,B,C] and [A,B,C,D] share a key.
// Commas (and the escape character) inside a target are escaped so ['a, b'] and ['a', 'b'] differ.
export function notifyGroupKey(monitor) {
  return [...new Set(monitor.notifyTargets || [])]
    .sort()
    .map((t) => String(t).replace(/([\\,])/g, '\\$1'))
    .join(',');
}

// Human-readable label for a monitor's notify grouping (never used as an identity).
export function notifyGroupLabel(monitor) {
  return [...new Set(monitor.notifyTargets || [])].sort().join(', ');
}

// Newest-first comparator on epoch ms (-Infinity = never triggered). Returns 0 when equal --
// plain subtraction gives NaN for -Infinity - -Infinity, which makes Array.sort order undefined.
export function compareNewestFirst(ta, tb) {
  if (ta === tb) return 0;
  return tb > ta ? 1 : -1;
}

export const lastTriggeredMs = (m) => (m.lastTriggered ? new Date(m.lastTriggered).getTime() : -Infinity);

const DAY_MS = 24 * 60 * 60 * 1000;

// Age buckets for the Triggered Info cards: a monitor is in a range when its time since last
// trigger is more than `minDays` (null = no lower bound) and at most `maxDays` (null = no upper bound).
export const TRIGGERED_RANGES = [
  { id: 'triggered3d', title: 'Past 3 days', centerLabel: 'triggered in 3 days', chip: 'Triggered in the past 3 days', minDays: null, maxDays: 3 },
  { id: 'triggered4to7d', title: '4 - 7 days ago', centerLabel: 'triggered 4 - 7 days ago', chip: 'Triggered 4 - 7 days ago', minDays: 3, maxDays: 7 },
  { id: 'triggered8to30d', title: '8 - 30 days ago', centerLabel: 'triggered 8 - 30 days ago', chip: 'Triggered 8 - 30 days ago', minDays: 7, maxDays: 30 },
  { id: 'triggeredOver30d', title: 'Over 30 days ago', centerLabel: 'triggered over 30 days ago', chip: 'Triggered over 30 days ago', minDays: 30, maxDays: null },
  { id: 'triggeredNever', title: 'Never triggered', centerLabel: 'never triggered', chip: 'Never triggered', never: true }
];

/** True when the monitor last triggered within `range`; the `never` range matches monitors with no last-triggered date. */
export const triggeredInRange = (monitor, { minDays, maxDays, never }) => {
  const last = lastTriggeredMs(monitor);
  if (never) return !Number.isFinite(last);
  if (!Number.isFinite(last)) return false;
  const age = Date.now() - last;
  return (minDays === null || age > minDays * DAY_MS) && (maxDays === null || age <= maxDays * DAY_MS);
};

const EVENT_WINDOW_MS = 4 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

/**
 * DataDog Event Explorer link for one monitor's alert + audit events (the monitor's own `url`
 * supplies the org host). Returns '' when the monitor has no id/host or has never triggered.
 * The window is the default last 4 hours, widened back to an hour before the last trigger when
 * that is older, so the event being linked from is always inside it; live=true keeps it sliding.
 */
export function monitorEventsUrl(monitor) {
  const lastMs = lastTriggeredMs(monitor);
  const host = (monitor.url || '').replace(/\/monitors\/[^/]*$/, '');
  if (!Number.isFinite(lastMs) || !host || monitor.id === undefined || monitor.id === null) return '';
  const id = encodeURIComponent(monitor.id);
  // encodeURIComponent leaves ( and ) alone; DataDog's own links encode them.
  const query = encodeURIComponent(
    `((source:alert OR source:monitor-alert-event) (@monitor_id:${id} OR monitor_id:${id})) `
    + `OR ((source:datadog OR source:monitor-audit-event) monitor_id:${id})`
  ).replace(/\(/g, '%28').replace(/\)/g, '%29');
  const now = Date.now();
  const from = Math.min(now - EVENT_WINDOW_MS, lastMs - HOUR_MS);
  return `${host}/event/explorer?query=${query}&agg_m=count&agg_m_source=base&agg_t=count`
    + '&clustering_pattern_field_path=message&cols=&messageDisplay=expanded-lg&options=&refresh_mode=sliding'
    + `&sort=DESC&from_ts=${from}&to_ts=${now}&live=true`;
}

const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

export function docCategory(monitor) {
  const hasNotify = (monitor.notifyTargets || []).length > 0;
  const hasRunbook = !!monitor.runbookUrl;
  if (hasNotify && hasRunbook) return 'Notify and Runbook';
  if (hasNotify) return 'Notify only';
  if (hasRunbook) return 'Runbook only';
  return 'No Notify or Runbook';
}

/** Keeps only monitors in the given alert state and drops groups left with none ('All' = no-op). */
export function filterGroupsByAlertState(groups, alertState) {
  if (alertState === 'All') return groups;
  return groups
    .map((g) => ({ ...g, monitors: g.monitors.filter((m) => m.alertState === alertState) }))
    .filter((g) => g.monitors.length > 0);
}

const countByAlertState = (rows, alertState) => rows.filter((m) => m.alertState === alertState).length;

// Buckets monitors into { key, label, monitors } groups (alphabetical by label, monitors sorted by
// name). `entriesOf(monitor)` returns the { key, label } groups the monitor belongs to -- none,
// one, or several.
function buildGroups(monitors, entriesOf) {
  const byKey = new Map();
  monitors.forEach((m) => {
    entriesOf(m).forEach(({ key, label }) => {
      if (!byKey.has(key)) byKey.set(key, { key, label, monitors: [] });
      byKey.get(key).monitors.push(m);
    });
  });
  return [...byKey.values()]
    .map((g) => ({ ...g, monitors: [...g.monitors].sort(byName) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Every distinct notify target of the monitors last triggered in `range`, as { key, label, monitors, kind }
 * sorted by name. A monitor with exactly one target is a solo notification, one with several is shared:
 * kind is 'uniqueOnly' (target only ever solo), 'sharedAndUnique' (both) or 'sharedOnly' (never solo).
 */
export function notifiedTargetGroups(monitors, range) {
  const byTarget = new Map();
  monitors.filter((m) => triggeredInRange(m, range)).forEach((m) => {
    const targets = [...new Set(m.notifyTargets || [])];
    targets.forEach((t) => {
      const entry = byTarget.get(t) || { key: t, label: t, monitors: [], solo: false, shared: false };
      entry.monitors.push(m);
      if (targets.length === 1) entry.solo = true; else entry.shared = true;
      byTarget.set(t, entry);
    });
  });
  return [...byTarget.values()]
    .map(({ solo, shared, ...group }) => ({ ...group, kind: solo && shared ? 'sharedAndUnique' : (solo ? 'uniqueOnly' : 'sharedOnly') }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// Counts per doc category for one slice of monitors.
function computeDocStats(rows) {
  const counts = Object.fromEntries(DOC_CATEGORIES.map((c) => [c, 0]));
  rows.forEach((m) => { counts[docCategory(m)] += 1; });
  return {
    total: rows.length,
    runbookOnly: counts['Runbook only'],
    runbookAndNotify: counts['Notify and Runbook'],
    notifyOnly: counts['Notify only'],
    neither: counts['No Notify or Runbook']
  };
}

// Distinct runbooks, split by which alert states their tied monitors are in.
function computeRunbooksDefinedStats(monitors) {
  const byUrl = new Map();
  monitors.forEach((m) => {
    if (!m.runbookUrl) return;
    const entry = byUrl.get(m.runbookUrl) || { active: false, paused: false };
    if (m.alertState === 'Active') entry.active = true;
    else if (m.alertState === 'Paused') entry.paused = true;
    byUrl.set(m.runbookUrl, entry);
  });
  const stats = { total: byUrl.size, activeOnly: 0, pausedOnly: 0, both: 0 };
  byUrl.forEach(({ active, paused }) => {
    if (active && paused) stats.both += 1;
    else if (active) stats.activeOnly += 1;
    else if (paused) stats.pausedOnly += 1;
  });
  return stats;
}

// Every overlap group (any size), each with its monitors sorted most-recently-triggered first,
// then the groups themselves sorted by the same rule as the Duplicate-filtered table: group size
// desc, most recent trigger desc, group number asc.
function sortDuplicateGroups(overlapGroups) {
  return (overlapGroups || [])
    .map((group) => ({
      ...group,
      monitors: [...group.monitors].sort((a, b) => compareNewestFirst(lastTriggeredMs(a), lastTriggeredMs(b)))
    }))
    .sort((a, b) => {
      const sizeDiff = b.monitors.length - a.monitors.length;
      if (sizeDiff !== 0) return sizeDiff;
      const recentDiff = compareNewestFirst(lastTriggeredMs(a.monitors[0]), lastTriggeredMs(b.monitors[0]));
      if (recentDiff !== 0) return recentDiff;
      return a.groupId - b.groupId;
    });
}

/**
 * Everything the donut cards and their popups display, derived from the loaded monitors and the
 * overlap results (`overlapGroups` is null until overlap detection finishes).
 *
 * - doc: { Active, Paused, All } -> { total, runbookOnly, runbookAndNotify, notifyOnly, neither }
 * - runbooksDefined: { total, activeOnly, pausedOnly, both }; runbooks: groups, one per runbook URL
 * - creators / notifyGroups / individualNotifyTargets: [{ key, label, monitors }] groups for the popups
 * - creatorStats / notifyGroupStats: { unique, active, paused }
 * - duplicates: { active, paused, total } (null while overlaps load); duplicateGroups: sorted groups
 * - triggered: TRIGGERED_RANGES id -> same shape as a `doc` entry, for monitors last triggered in that range
 * - recentlyRun: same as `triggered`, limited to monitors that have a runbook, plus `runbooks` (distinct runbook count)
 * - recentlyNotified: TRIGGERED_RANGES id -> { total, uniqueOnly, sharedAndUnique, sharedOnly } counts of distinct notify targets
 */
export function useMonitorStats(monitors, overlapGroups, overlapByMonitorId) {
  const doc = useMemo(() => {
    const result = Object.fromEntries(
      ALL_ALERT_STATES.map((state) => [state, computeDocStats(monitors.filter((m) => m.alertState === state))])
    );
    result.All = computeDocStats(monitors);
    return result;
  }, [monitors]);

  const runbooksDefined = useMemo(() => computeRunbooksDefinedStats(monitors), [monitors]);

  const creators = useMemo(
    () => buildGroups(monitors, (m) => {
      const creator = m.creator || '(unknown)';
      return [{ key: creator, label: creator }];
    }),
    [monitors]
  );

  // Unique notify-target groupings (order-insensitive); monitors with no targets don't count.
  const notifyGroups = useMemo(
    () => buildGroups(monitors, (m) => ((m.notifyTargets || []).length ? [{ key: notifyGroupKey(m), label: notifyGroupLabel(m) }] : [])),
    [monitors]
  );

  // Each notify target on its own (a monitor with several targets appears under every one).
  // Keys are prefixed so expanded state stays separate from the grouping tabs (a single-target
  // grouping has the same label as its individual).
  const individualNotifyTargets = useMemo(
    () => buildGroups(monitors, (m) => [...new Set(m.notifyTargets || [])].map((t) => ({ key: `ind:${t}`, label: t }))),
    [monitors]
  );

  // One group per distinct runbook URL with every monitor tied to it, most monitors first.
  const runbooks = useMemo(
    () => buildGroups(monitors, (m) => (m.runbookUrl ? [{ key: m.runbookUrl, label: m.runbookUrl }] : []))
      .sort((a, b) => b.monitors.length - a.monitors.length || a.label.localeCompare(b.label)),
    [monitors]
  );

  const creatorStats = useMemo(() => ({
    unique: creators.length,
    active: countByAlertState(monitors, 'Active'),
    paused: countByAlertState(monitors, 'Paused')
  }), [creators, monitors]);

  // Active/Paused are monitor counts among monitors that have at least one notify target.
  const notifyGroupStats = useMemo(() => {
    const withTargets = monitors.filter((m) => (m.notifyTargets || []).length > 0);
    return {
      unique: notifyGroups.length,
      active: countByAlertState(withTargets, 'Active'),
      paused: countByAlertState(withTargets, 'Paused')
    };
  }, [monitors, notifyGroups]);

  // Monitors flagged as overlapping with at least one other monitor, split by alert state.
  const duplicates = useMemo(() => {
    if (overlapGroups === null) return null;
    const duplicated = monitors.filter((m) => overlapByMonitorId.has(m.id));
    const active = countByAlertState(duplicated, 'Active');
    const paused = countByAlertState(duplicated, 'Paused');
    return { active, paused, total: active + paused };
  }, [monitors, overlapByMonitorId, overlapGroups]);

  const duplicateGroups = useMemo(() => sortDuplicateGroups(overlapGroups), [overlapGroups]);

  // Monitors last triggered within each TRIGGERED_RANGES bucket, split by doc category.
  const triggered = useMemo(
    () => Object.fromEntries(
      TRIGGERED_RANGES.map((range) => [range.id, computeDocStats(monitors.filter((m) => triggeredInRange(m, range)))])
    ),
    [monitors]
  );

  // Same buckets, restricted to monitors that have a runbook.
  // `runbooks` is the number of distinct runbook URLs among them; the other counts are monitors.
  const recentlyRun = useMemo(
    () => Object.fromEntries(
      TRIGGERED_RANGES.map((range) => {
        const inRange = monitors.filter((m) => m.runbookUrl && triggeredInRange(m, range));
        return [range.id, { ...computeDocStats(inRange), runbooks: new Set(inRange.map((m) => m.runbookUrl)).size }];
      })
    ),
    [monitors]
  );

  // Per range, the distinct notify targets of the monitors triggered in it, classified by how they
  // were notified: a monitor with exactly one target is a solo notification, one with several is shared.
  const recentlyNotified = useMemo(
    () => Object.fromEntries(
      TRIGGERED_RANGES.map((range) => {
        const targets = notifiedTargetGroups(monitors, range);
        const count = (kind) => targets.filter((t) => t.kind === kind).length;
        return [range.id, {
          total: targets.length, uniqueOnly: count('uniqueOnly'), sharedAndUnique: count('sharedAndUnique'), sharedOnly: count('sharedOnly')
        }];
      })
    ),
    [monitors]
  );

  return useMemo(() => ({
    doc, runbooksDefined, runbooks, creators, notifyGroups, individualNotifyTargets,
    creatorStats, notifyGroupStats, duplicates, duplicateGroups, triggered, recentlyRun, recentlyNotified
  }), [doc, runbooksDefined, runbooks, creators, notifyGroups, individualNotifyTargets, creatorStats, notifyGroupStats, duplicates, duplicateGroups, triggered, recentlyRun, recentlyNotified]);
}
